// V2 版式：ledger（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/ledger.ts 逐坐标移植
// 纵向账单行：左项目名、中间 hairline 虚位、右数字右对齐（fontMono 醒目）；
// 行间 hairline 分隔；末行若为合计语义项（合计/总计/小计/total）渲染为加粗 accent 行。
// 几何取 V2GEO["ledger"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { isLuxe } from "./luxe";
import { consultTheme, brutalTheme, termTheme } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 ledger.ts GEO 一一对应
const GEO = V2GEO["ledger"] as any;

// ledger block 结构以后端 _shared/pptx/types.ts 为准（前端 slides.ts Block 同步后此处收窄仍成立）
type LedgerItem = { name: string; value: string; note?: string };
type LedgerBlock = { type: "ledger"; items: LedgerItem[] };

/** 合计语义判定——同后端 isTotal */
function isTotal(name: string): boolean {
  return /合计|总计|小计|total/i.test(name);
}

export default function V2Ledger({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // luxe：数字列用 fontHero 衬线英数（ledger.ts 分支），hairline 细线纪律不变
  const luxe = isLuxe(skin);
  // brutal：报纸 boxed 表格 3px 粗黑框；term：boxed 面板 1px 细框；consult：合计行 emerald（ledger.ts:69-80,120）
  const consultT = consultTheme(skin);
  const brutalT = brutalTheme(skin);
  const termT = termTheme(skin);

  const li = c.blocks.findIndex((b) => (b as { type: string }).type === "ledger");
  const ledger = li >= 0 ? (c.blocks[li] as unknown as LedgerBlock) : null;
  const items = ledger ? ledger.items : [];

  const n = items.length;
  const tableH = GEO.table.bottom - GEO.table.top;
  const rowH = n > 0 ? Math.min(tableH / n, GEO.maxRowH) : 0;
  const valueX = W - m - GEO.valueW;
  const hasNote = items.some((it) => it.note);
  const noteX = m + GEO.nameW + 0.15;
  // 虚位 hairline 区：项目名/备注列之后 → 数值列之前
  const leaderX = hasNote ? noteX + GEO.noteW + GEO.leaderGap : m + GEO.nameW + GEO.leaderGap;
  const leaderW = valueX - GEO.leaderGap - leaderX;
  // 合计行：仅当末行是合计语义项时特殊渲染
  const totalIdx = n > 0 && isTotal(items[n - 1].name) ? n - 1 : -1;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "ledger")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {/* 表外框：brutal 报纸 boxed 表格 3px 粗黑框；term boxed 面板 1px 细框（ledger.ts:69-80） */}
      {brutalT && (
        <div style={{
          position: "absolute", left: px(m - 0.14), top: px(GEO.table.top - 0.12),
          width: px(W - 2 * m + 0.28), height: px(tableH + 0.24),
          border: `3px solid ${brutalT.ink}`,
        }} />
      )}
      {termT && (
        <div style={{
          position: "absolute", left: px(m - 0.12), top: px(GEO.table.top - 0.1),
          width: px(W - 2 * m + 0.24), height: px(tableH + 0.2),
          border: `1px solid ${termT.muted}`,
        }} />
      )}

      {items.map((it, i) => {
        const rowY = GEO.table.top + i * rowH;
        const isSum = i === totalIdx;
        const midY = rowY + rowH / 2;
        return (
          <div key={i}>
            {/* 合计行上方一条 accent 粗线（2×hairline；brutal 3× 粗红） */}
            {isSum && (
              <div style={{
                position: "absolute", left: px(m), top: px(rowY - SPACING.hairline),
                width: px(W - 2 * m), height: px(brutalT ? SPACING.hairline * 3 : SPACING.hairline * 2),
                background: skin.accent,
                borderRadius: mag ? px(skin.radius) : 0,
              }} />
            )}

            {/* 左项目名（valign middle） */}
            <EditableText
              value={it.name} path={`blocks.${li}.items.${i}.name`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(m), top: px(rowY),
                width: px(GEO.nameW), height: px(rowH),
                fontSize: pt(isSum ? skin.scale.sectionHead : skin.scale.body),
                color: isSum ? skin.accent : skin.text,
                fontFamily: skin.fontBody, fontWeight: isSum ? 700 : 400,
                display: "flex", alignItems: "center",
              }}
            />

            {/* 备注（可选，次要色小字） */}
            {hasNote && (it.note || editable) && (
              <EditableText
                value={it.note ?? ""} path={`blocks.${li}.items.${i}.note`} editable={editable}
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(noteX), top: px(rowY),
                  width: px(GEO.noteW), height: px(rowH),
                  fontSize: pt(skin.scale.caption), color: skin.sub, fontFamily: skin.fontBody,
                  display: "flex", alignItems: "center",
                }}
              />
            )}

            {/* 中间 hairline 虚位（行中线，合计行不画） */}
            {!isSum && leaderW > 0.3 && (
              <div style={{
                position: "absolute", left: px(leaderX), top: px(midY),
                width: px(leaderW), height: px(SPACING.hairline),
                background: skin.hairline,
              }} />
            )}

            {/* 右数字：fontMono 醒目、右对齐（luxe 用衬线 fontHero） */}
            <EditableText
              value={it.value} path={`blocks.${li}.items.${i}.value`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(valueX), top: px(rowY),
                width: px(GEO.valueW), height: px(rowH),
                fontSize: pt(isSum ? Math.round(skin.scale.sectionHead * 1.25) : skin.scale.sectionHead),
                color: isSum && consultT ? consultT.accentEmerald : isSum ? skin.accent : skin.text,
                fontFamily: luxe ? skin.fontHero : skin.fontMono, fontWeight: isSum ? 700 : 400,
                textAlign: "right", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", justifyContent: "flex-end",
              }}
            />

            {/* 行间 hairline 分隔（合计行后不再画） */}
            {i < n - 1 && i + 1 !== totalIdx && (
              <div style={{
                position: "absolute", left: px(m), top: px(rowY + rowH),
                width: px(W - 2 * m), height: px(SPACING.hairline),
                background: skin.hairline,
              }} />
            )}
          </div>
        );
      })}
    </>
  );
}
